import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, Leaf, Shield, Heart, Star } from 'lucide-react'
import { supabase } from '../lib/supabase'
import ProductCard from '../components/product/ProductCard'
import Button from '../components/ui/Button'
import SEO from '../components/SEO'

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 },
}

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
}

const features = [
  {
    icon: Leaf,
    title: 'Natural Materials',
    description: '100% food-grade silicone, free from BPA, PVC, and phthalates.',
  },
  {
    icon: Shield,
    title: 'Safety Tested',
    description: 'Exceeds Australian safety standards. Rigorously tested for your peace of mind.',
  },
  {
    icon: Heart,
    title: 'Developmentally Designed',
    description: 'Created with pediatric therapists to support sensory development.',
  },
]

export default function Home() {
  const [featuredProducts, setFeaturedProducts] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetchFeaturedProducts()
  }, [])

  const fetchFeaturedProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('featured', true)
        .eq('in_stock', true)
        .limit(3)

      if (error) throw error
      setFeaturedProducts(data || [])
    } catch (error) {
      console.error('Error fetching featured products:', error)
      setFeaturedProducts([])
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="overflow-hidden">
      <SEO url="/" />
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-gradient-to-b from-pp-linen to-pp-ivory">
        <div className="absolute inset-0 grain-overlay opacity-30" />
        <div className="container mx-auto px-4 lg:px-8 py-20 lg:py-32 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial="initial"
              animate="animate"
              variants={staggerContainer}
            >
              <motion.p
                variants={fadeInUp}
                className="text-pp-terracotta font-body text-sm tracking-widest uppercase mb-4"
              >
                Real Food Shapes, Real Science
              </motion.p>
              <motion.h1
                variants={fadeInUp}
                className="font-display text-display-lg lg:text-display-xl text-pp-ink mb-6"
              >
                Nurturing little
                <span className="text-pp-terracotta"> palates</span>,
                <br />
                one texture at a time
              </motion.h1>
              <motion.p
                variants={fadeInUp}
                className="text-pp-stone text-lg leading-relaxed mb-8 max-w-lg"
              >
                Beautifully crafted teethers shaped like the foods your baby will
                meet first — apple, banana, and carrot. Designed around oral
                exploration science with graduated textures for a purposeful
                journey of discovery.
              </motion.p>
              <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
                <Button as={Link} to="/shop" size="lg">
                  Shop Collection
                  <ArrowRight className="w-5 h-5" />
                </Button>
                <Button as={Link} to="/science" variant="outline" size="lg">
                  The Science
                </Button>
              </motion.div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-square rounded-3xl overflow-hidden">
                <img
                  src="https://rjzpwxuynzdiqehcdddj.supabase.co/storage/v1/object/public/product-images/products/mother%20and%20baby.jpeg"
                  alt="Mother and baby enjoying Petite Palate teethers"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-pp-sage-soft/40 rounded-full blur-2xl" />
              <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-pp-terracotta-soft/30 rounded-full blur-3xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 lg:py-24 bg-pp-ivory">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8 lg:gap-12"
          >
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                variants={fadeInUp}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-pp-sage-soft/50 text-pp-sage mb-4">
                  <feature.icon className="w-7 h-7" />
                </div>
                <h3 className="font-display text-xl text-pp-ink mb-2">
                  {feature.title}
                </h3>
                <p className="text-pp-stone text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 lg:py-24 bg-pp-linen">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-display text-display-md text-pp-ink mb-4">
              Our First Foods Collection
            </h2>
            <p className="text-pp-stone max-w-lg mx-auto">
              Meet the three foods parents reach for first — now reimagined as
              sensory teethers backed by developmental science.
            </p>
          </motion.div>

          {isLoading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-pp-sand/30 rounded-2xl aspect-square mb-4"></div>
                  <div className="h-4 bg-pp-sand/30 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-pp-sand/30 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : (
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
            >
              {featuredProducts.map((product) => (
                <motion.div key={product.id} variants={fadeInUp}>
                  <ProductCard product={product} />
                </motion.div>
              ))}
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button as={Link} to="/shop" variant="outline">
              View All Products
              <ArrowRight className="w-4 h-4" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Science Callout */}
      <section className="py-16 lg:py-24 bg-pp-ivory">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="font-display text-display-sm text-pp-ink mb-4">
              Designed around oral exploration science
            </h2>
            <p className="text-pp-stone mb-6 leading-relaxed">
              The infant mouth is one of the most densely innervated areas of the
              body. Our teethers are shaped like real foods because studies show
              that visual familiarity builds the recognition that supports later
              food acceptance.
            </p>
            <Button as={Link} to="/science" variant="outline">
              Discover the Science
              <ArrowRight className="w-4 h-4" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-16 lg:py-24 bg-pp-linen">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <div className="flex justify-center gap-1 mb-6">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className="w-5 h-5 fill-pp-butter text-pp-butter"
                />
              ))}
            </div>
            <blockquote className="font-display text-display-sm text-pp-ink mb-6 italic">
              "These teethers have been a game-changer for us. My little one is
              obsessed with the textures, and I love knowing they're safe and
              developmental."
            </blockquote>
            <cite className="text-pp-stone not-italic">
              — Sarah M., Melbourne
            </cite>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-pp-charcoal text-white">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-display-md mb-4">
              Ready to explore?
            </h2>
            <p className="text-pp-sand mb-8 max-w-lg mx-auto">
              Join thousands of Australian parents who trust Petite Palate for
              their little one's sensory journey.
            </p>
            <Button
              as={Link}
              to="/shop"
              variant="secondary"
              size="lg"
              className="bg-pp-terracotta hover:bg-pp-terracotta/90 border-pp-terracotta"
            >
              Shop Now
              <ArrowRight className="w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
